import React, { useEffect, useMemo, useState } from "react";

// API e contexto
import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import Draggable from "react-draggable";

// MUI
import {
  Card,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  FormControlLabel,
  Tabs,
  Tab,
  Switch,
  Checkbox,
  Paper,
} from "@mui/material";

import InfoIcon from "@mui/icons-material/InfoOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

// Layout JFB (Creative Tim)
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// PageHeader (título/subtítulo vêm de routes.js)
import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

// Dialog reutilizável de Stocks/Vendas/Compras do material
import MenuSearchMateriais from "../components/MenuSearchMateriais";

// Autocomplete para Fabricantes
import Autocomplete from "@mui/material/Autocomplete";

// =============================================================================
// TabPanel padrão MUI (a versão recomendada pela documentação)
// =============================================================================
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analise-tabpanel-${index}`}
      aria-labelledby={`analise-tab-${index}`}
      {...other}
      style={{ paddingTop: 8 }}
    >
      {value === index && <MDBox>{children}</MDBox>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `analise-tab-${index}`,
    "aria-controls": `analise-tabpanel-${index}`,
  };
}

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
function AnaliseStocks() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");
  const isComercial = temFuncao("Comercial");

  const refVendedorAuth = (userData?.refvendedor || userData?.ref || userData?.refutilizador || "")
    .toString()
    .trim();
  const idUtilizadorAuth = Number(userData?.id || 0);

  // Centros associados ao utilizador (refs)
  const [centrosUserRefs, setCentrosUserRefs] = useState([]);

  const [pendEmailEnc, setPendEmailEnc] = useState(0);
  const [pendEmailPre, setPendEmailPre] = useState(0);

  // Loading global
  const [loading, setLoading] = useState(false);
  const [analisePronta, setAnalisePronta] = useState(false);

  // Fabricantes
  const [fabricantes, setFabricantes] = useState([]);
  const [fabricanteSel, setFabricanteSel] = useState(null);

  // Materiais apresentados na tabela
  const [rows, setRows] = useState([]);

  // Dialogs
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockDialogRow, setStockDialogRow] = useState(null);

  const [detalhesOpen, setDetalhesOpen] = useState(false);
  const [materialDetalhe, setMaterialDetalhe] = useState(null);

  const [encomendasOpen, setEncomendasOpen] = useState(false);
  const [encomendasMat, setEncomendasMat] = useState(null);
  const [encomendasRows, setEncomendasRows] = useState([]);
  const [encomendaForm, setEncomendaForm] = useState({
    id: 0,
    refcentro: "",
    quantidade: "",
    obs: "",
  });

  const [preVendasOpen, setPreVendasOpen] = useState(false);
  const [preVendasMat, setPreVendasMat] = useState(null);
  const [preVendasRows, setPreVendasRows] = useState([]);
  const [preVendaForm, setPreVendaForm] = useState({
    id: 0,
    refcentro: "",
    quantidade: "",
    obs: "",
  });

  const [mostrarInativos, setMostrarInativos] = useState(false);

  const [openDialogInativar, setOpenDialogInativar] = useState(false);
  const [materialSelecionado, setMaterialSelecionado] = useState(null);
  const [openDialogAtivar, setOpenDialogAtivar] = useState(false);

  const [imgsByRef, setImgsByRef] = useState({}); // { "2006430": "http://..." }

  const [confirmDelEncOpen, setConfirmDelEncOpen] = useState(false);
  const [encToDelete, setEncToDelete] = useState(null);

  const [confirmDelPreOpen, setConfirmDelPreOpen] = useState(false);
  const [preToDelete, setPreToDelete] = useState(null);
  const [pendentesOpen, setPendentesOpen] = useState(false);
  const [pendentesRows, setPendentesRows] = useState([]);
  const [pendentesLoading, setPendentesLoading] = useState(false);
  const [pendentesVerTodos, setPendentesVerTodos] = useState(false);

  const carregarPendentesEmail = async () => {
    try {
      const [dados, err] = await handleApi(
        api.post("Materiais/SelPendentesEmailMateriais", { refvendedor: refVendedorAuth })
      );
      if (err) return;

      // Pode vir:
      //  A) { success:true, data:{...} }   (envelope)
      //  B) { encomendas:2, prevendas:2 } (já normalizado pelo handleApi)
      //  C) axios response (raro aqui): { data: { success:true, data:{...} } }

      const envelope =
        dados?.success !== undefined
          ? dados
          : dados?.data?.success !== undefined
          ? dados.data
          : null;

      const data = envelope?.data ?? dados?.data ?? dados;

      setPendEmailEnc(Number(data?.encomendas || 0));
      setPendEmailPre(Number(data?.prevendas || 0));
    } catch {
      // ignora
    }
  };

  const carregarImagens = async (refs) => {
    const lista = (refs || []).map((r) => String(r || "").trim()).filter(Boolean);

    // só pedir as refs que ainda não temos em cache
    const faltam = lista.filter((r) => !imgsByRef[r]);
    if (faltam.length === 0) return;

    try {
      const [dados, err] = await handleApi(
        api.post("Materiais/SelMateriaisImagens", { refs: faltam })
      );
      if (err) {
        // não bloquear UI por falha de imagens
        console.warn("Falha ao carregar imagens:", err);
        return;
      }

      const mapa = {};
      (Array.isArray(dados) ? dados : []).forEach((x) => {
        const ref = String(x?.refmaterial || "").trim();
        const url = String(x?.url || "").trim();
        if (ref && url) mapa[ref] = url;
      });

      setImgsByRef((prev) => ({ ...prev, ...mapa }));
    } catch (e) {
      console.warn("Falha ao carregar imagens:", e);
    }
  };

  // ------------------------------------------------------------
  // ★ ESTADOS PARA TABS E FILTROS POR AGRUPAMENTO ★
  // ------------------------------------------------------------

  // Tab selecionada (0 = Tipos, 1 = Famílias, 2 = Marcas, 3 = Centros)
  const [tabIndex, setTabIndex] = useState(3);

  // Valores existentes (possíveis) — gerados dinamicamente
  const [tiposPossiveis, setTiposPossiveis] = useState([]);
  const [familiasPossiveis, setFamiliasPossiveis] = useState([]);
  const [marcasPossiveis, setMarcasPossiveis] = useState([]);
  const [centrosPossiveis, setCentrosPossiveis] = useState([]);

  // Valores selecionados pelo utilizador
  const [tiposSel, setTiposSel] = useState([]);
  const [familiasSel, setFamiliasSel] = useState([]);
  const [marcasSel, setMarcasSel] = useState([]);
  const [centrosSel, setCentrosSel] = useState([]);

  const [dialogDiasRutura, setDialogDiasRutura] = useState({
    open: false,
    refmaterial: null,
    nome: "",
    valorNovo: null,
    valorAntigo: null,
  });

  useEffect(() => {
    carregarPendentesEmail();
  }, [userData]);

  // Carregar centros associados ao utilizador (para defaults no separador Centros)
  useEffect(() => {
    const carregarCentrosUtilizador = async () => {
      if (!idUtilizadorAuth || idUtilizadorAuth <= 0) {
        setCentrosUserRefs([]);
        return;
      }

      setLoading(true);
      try {
        const [dados, err] = await handleApi(
          api.post("Materiais/SelCentrosUtilizador", { idutilizador: idUtilizadorAuth })
        );

        if (err) {
          // se falhar, comporta-se como "sem centros" (ativa todos)
          setCentrosUserRefs([]);
          return;
        }

        const refs = (Array.isArray(dados) ? dados : [])
          .map((x) => String(x?.refcentro || "").trim())
          .filter(Boolean);

        setCentrosUserRefs(refs);
      } catch {
        setCentrosUserRefs([]);
      } finally {
        setLoading(false);
      }
    };

    carregarCentrosUtilizador();
  }, [idUtilizadorAuth]);

  // =============================================================================
  // CARREGAR FABRICANTES AO MONTAR
  // =============================================================================
  useEffect(() => {
    const carregarFabricantes = async () => {
      setLoading(true);
      try {
        const [dados, err] = await handleApi(
          api.post(
            isAdminLike ? "Fabricantes/SelFabricantesCombo" : "Comercial/SelFabricantesCombo",
            {}
          )
        );
        if (err) {
          showAlert(err, "error");
          setFabricantes([]);
          return;
        }
        setFabricantes(Array.isArray(dados) ? dados : []);
      } catch {
        showAlert("Erro ao carregar fabricantes.", "error");
      } finally {
        setLoading(false);
      }
    };

    carregarFabricantes();
  }, [showAlert]);

  // =============================================================================
  // CARREGAR ANÁLISE DE STOCKS PARA O FABRICANTE SELECIONADO
  // =============================================================================
  const carregarAnalise = async (refFabricante) => {
    if (!refFabricante) {
      setRows([]);
      setAnalisePronta(false);
      return;
    }

    setRows([]);
    setAnalisePronta(false);

    setLoading(true);
    try {
      const body = {
        reffabricante: refFabricante,
        mostrarInativos: mostrarInativos ? 1 : 0,
        refCentros: Array.isArray(centrosSel) ? centrosSel : [],
        refTipos: Array.isArray(tiposSel) ? tiposSel : [],
        refFamilias: Array.isArray(familiasSel) ? familiasSel : [],
        refMarcas: Array.isArray(marcasSel) ? marcasSel : [],
        idutilizador: idUtilizadorAuth,
      };

      console.log("SelAnaliseStocksPorFabricante body:", body);

      const [dados, err] = await handleApi(
        api.post("Materiais/SelAnaliseStocksPorFabricante", body)
      );
      if (err) {
        showAlert(err, "error");
        setRows([]);
        return;
      }

      const lista = Array.isArray(dados)
        ? dados.map((r) => ({
            ...r,
            // garante número 0/1 mesmo que a API envie como string ou long
            inativo: r.inativo === null || r.inativo === undefined ? 0 : Number(r.inativo),
          }))
        : [];

      // console.log("AnaliseStocks – dados recebidos:", lista);

      setRows(lista);

      const refs = lista.map((m) => m.refmaterial).filter(Boolean);
      await carregarImagens(refs);

      // ------------------------------------------------------------------------
      // ★ Gerar os valores POSSÍVEIS iniciais com base nos dados recebidos ★
      // ------------------------------------------------------------------------
      const tipos = new Map();
      const familias = new Map();
      const marcas = new Map();
      const centros = new Map();

      lista.forEach((m) => {
        if (m.refmaterialtipo)
          tipos.set(m.refmaterialtipo, m.nomematerialtipo || m.refmaterialtipo);
        if (m.refmaterialfamilia)
          familias.set(m.refmaterialfamilia, m.nomematerialfamilia || m.refmaterialfamilia);
        if (m.refmaterialmarca)
          marcas.set(m.refmaterialmarca, m.nomematerialmarca || m.refmaterialmarca);

        // Centros de stock deste material
        (m.stocks_centros || []).forEach((s) => {
          if (s.refcentro) {
            const nomeCentro = s.centro || s.refcentro;
            centros.set(s.refcentro, nomeCentro);
          }
        });
      });

      const novosTipos = [...tipos.entries()];
      const novasFamilias = [...familias.entries()];
      const novasMarcas = [...marcas.entries()];
      const novosCentros = [...centros.entries()];

      const refsTipos = novosTipos.map(([ref]) => ref);
      const refsFamilias = novasFamilias.map(([ref]) => ref);
      const refsMarcas = novasMarcas.map(([ref]) => ref);
      const refsCentros = novosCentros.map(([ref]) => ref);

      setTiposPossiveis(novosTipos);
      setFamiliasPossiveis(novasFamilias);
      setMarcasPossiveis(novasMarcas);
      setCentrosPossiveis(novosCentros);

      // Atualiza seleções em função dos valores realmente disponíveis
      // 1) Mantém apenas filtros ainda válidos (interseção)
      // 2) Se ficar vazio, aplica defaults:
      //    - Tipos: FERT (se existir) ou todos
      //    - Famílias / Marcas / Centros: todos
      setTiposSel((prev) => {
        const inter = prev.filter((x) => refsTipos.includes(x));
        if (inter.length > 0) return inter;
        if (refsTipos.length === 0) return [];
        if (refsTipos.includes("FERT")) return ["FERT"];
        return refsTipos;
      });

      setFamiliasSel((prev) => {
        const inter = prev.filter((x) => refsFamilias.includes(x));
        if (inter.length > 0) return inter;
        return refsFamilias;
      });

      setMarcasSel((prev) => {
        const inter = prev.filter((x) => refsMarcas.includes(x));
        if (inter.length > 0) return inter;
        return refsMarcas;
      });

      setCentrosSel((prev) => {
        const inter = prev.filter((x) => refsCentros.includes(x));
        if (inter.length > 0) return inter;

        // Se o utilizador tiver centros associados, ativa só esses (interseção com os disponíveis)
        if (Array.isArray(centrosUserRefs) && centrosUserRefs.length > 0) {
          const interUser = centrosUserRefs.filter((x) => refsCentros.includes(x));
          return interUser;
        }

        // Se não tiver centros associados, ativa todos
        return refsCentros;
      });
    } catch {
      showAlert("Erro ao carregar análise de stocks.", "error");
      setRows([]);
    } finally {
      setLoading(false);

      if (refFabricante) {
        setTimeout(() => {
          setAnalisePronta(true);
        }, 0);
      }
    }
  };

  // =============================================================================
  // HANDLER DE MUDANÇA DE FABRICANTE
  // =============================================================================
  const handleChangeFabricante = (_, value) => {
    // Apenas atualiza o fabricante selecionado.
    // O useEffect abaixo é que trata de chamar carregarAnalise,
    // evitando chamadas duplicadas à API.
    setAnalisePronta(false);
    setFabricanteSel(value);
  };

  // Sempre que o switch "Mostrar materiais inativos" mudar,
  // recarrega a análise para o fabricante atual.
  useEffect(() => {
    const ref = fabricanteSel?.ref;
    if (!ref) return;
    carregarAnalise(ref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarInativos, fabricanteSel]);

  const handleRecalcular = () => {
    const ref = fabricanteSel?.ref;
    if (!ref) return;
    carregarAnalise(ref);
  };

  const carregarPendentesDialog = async (verTodos = pendentesVerTodos) => {
    setPendentesLoading(true);

    const payload = {
      reffabricante: fabricanteSel?.ref || "",
      vertodos: verTodos ? 1 : 0,
    };

    const [dados, err, meta] = await handleApi(
      api.post("Materiais/SelMateriaisPendentesDialog", payload)
    );

    if (err) {
      showAlert(meta?.message || err || "Erro ao carregar pendentes.", "error");
      setPendentesRows([]);
      setPendentesLoading(false);
      return;
    }

    setPendentesRows(Array.isArray(dados) ? dados : []);
    setPendentesLoading(false);
  };

  const handleOpenPendentes = async () => {
    const verTodosInicial = !fabricanteSel?.ref;

    setPendentesVerTodos(verTodosInicial);
    setPendentesOpen(true);

    await carregarPendentesDialog(verTodosInicial);
  };

  const handleClosePendentes = () => {
    setPendentesOpen(false);
    setPendentesRows([]);
  };

  const handleToggleVerTodosPendentes = async (event) => {
    const novoValor = event.target.checked;
    setPendentesVerTodos(novoValor);
    await carregarPendentesDialog(novoValor);
  };

  // =============================================================================
  // HANDLER DE MUDANÇA DE TAB
  // =============================================================================
  const handleChangeTab = (_, newIndex) => {
    setTabIndex(newIndex);
  };

  // ---------------------------------------------------------------------------
  // HANDLERS DOS CHECKBOXES POR AGRUPAMENTO
  // Cada toggle altera apenas o array selecionado (ex.: tiposSel), e depois
  // chamamos recalcularFiltros() que será definido na Parte 2.
  // ---------------------------------------------------------------------------

  const toggleTipo = (ref) => {
    setTiposSel((prev) => (prev.includes(ref) ? prev.filter((x) => x !== ref) : [...prev, ref]));
  };

  const toggleFamilia = (ref) => {
    setFamiliasSel((prev) => (prev.includes(ref) ? prev.filter((x) => x !== ref) : [...prev, ref]));
  };

  const toggleMarca = (ref) => {
    setMarcasSel((prev) => (prev.includes(ref) ? prev.filter((x) => x !== ref) : [...prev, ref]));
  };

  const toggleCentro = (refCentro) => {
    setCentrosSel((prev) =>
      prev.includes(refCentro) ? prev.filter((x) => x !== refCentro) : [...prev, refCentro]
    );
  };

  // “Selecionar todos” em cada tab
  const handleToggleAllTipos = () => {
    const allRefs = tiposPossiveis.map(([ref]) => ref);
    setTiposSel((prev) => (prev.length === allRefs.length ? [] : allRefs));
  };

  const handleToggleAllFamilias = () => {
    const allRefs = familiasPossiveis.map(([ref]) => ref);
    setFamiliasSel((prev) => (prev.length === allRefs.length ? [] : allRefs));
  };

  const handleToggleAllMarcas = () => {
    const allRefs = marcasPossiveis.map(([ref]) => ref);
    setMarcasSel((prev) => (prev.length === allRefs.length ? [] : allRefs));
  };

  const handleToggleAllCentros = () => {
    const allRefs = centrosPossiveis.map(([ref]) => ref);
    setCentrosSel((prev) => (prev.length === allRefs.length ? [] : allRefs));
  };

  // ---------------------------------------------------------------------------
  // SECÇÃO VISUAL DAS TABS COM CHECKBOXES
  // Cada TabPanel mostra a lista de opções possíveis + checkboxes.
  // A cor do indicador das Tabs = vermelho JFB (igual ao PageHeader).
  // ---------------------------------------------------------------------------

  const renderCheckboxList = (lista, selecionados, toggleFn, allSelected, onToggleAll) => (
    <MDBox
      display="flex"
      flexWrap="wrap"
      flexDirection="row"
      gap={1}
      sx={{
        maxHeight: 240,
        overflowY: "auto",
        border: "1px solid #eee",
        borderRadius: 2,
        p: 1,
        backgroundColor: "#fafafa",
      }}
    >
      {lista.length === 0 && (
        <MDTypography sx={{ fontSize: 12, color: "#999" }}>Sem opções disponíveis.</MDTypography>
      )}

      {lista.length > 0 && (
        <MDBox sx={{ width: "100%", mb: 1 }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={allSelected} onChange={onToggleAll} />}
            label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todos</span>}
          />
        </MDBox>
      )}

      {lista.map(([ref, nome]) => (
        <MDBox key={ref} sx={{ minWidth: 250 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={selecionados.includes(ref)}
                onChange={() => toggleFn(ref)}
              />
            }
            label={
              <span style={{ fontSize: 13 }}>
                {nome} <span style={{ color: "#9e9e9e" }}>({ref})</span>
              </span>
            }
          />
        </MDBox>
      ))}
    </MDBox>
  );

  // ---------------------------------------------------------------------------
  // JSX COMPLETO DAS TABS
  // ---------------------------------------------------------------------------

  const tabsSecao = (
    <MDBox px={2} pb={1} pt={1}>
      <Tabs
        value={tabIndex}
        onChange={handleChangeTab}
        aria-label="tabs-analise"
        textColor="inherit"
        TabIndicatorProps={{
          style: {
            backgroundColor: "#d32f2f", // vermelho JFB (igual PageHeader)
            height: 3,
          },
        }}
        sx={{
          "& .MuiTab-root": {
            fontSize: 13,
            textTransform: "none",
            minHeight: 42,
          },
          "& .Mui-selected": {
            color: "#d32f2f !important",
            fontWeight: 600,
          },
        }}
      >
        <Tab label="Tipos" {...a11yProps(0)} />
        <Tab label="Famílias" {...a11yProps(1)} />
        <Tab label="Marcas" {...a11yProps(2)} />
        <Tab label="Centros" {...a11yProps(3)} />
      </Tabs>

      <TabPanel value={tabIndex} index={0}>
        {renderCheckboxList(
          tiposPossiveis,
          tiposSel,
          toggleTipo,
          tiposPossiveis.length > 0 && tiposSel.length === tiposPossiveis.length,
          handleToggleAllTipos
        )}
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        {renderCheckboxList(
          familiasPossiveis,
          familiasSel,
          toggleFamilia,
          familiasPossiveis.length > 0 && familiasSel.length === familiasPossiveis.length,
          handleToggleAllFamilias
        )}
      </TabPanel>

      <TabPanel value={tabIndex} index={2}>
        {renderCheckboxList(
          marcasPossiveis,
          marcasSel,
          toggleMarca,
          marcasPossiveis.length > 0 && marcasSel.length === marcasPossiveis.length,
          handleToggleAllMarcas
        )}
      </TabPanel>

      <TabPanel value={tabIndex} index={3}>
        {renderCheckboxList(
          centrosPossiveis,
          centrosSel,
          toggleCentro,
          centrosPossiveis.length > 0 && centrosSel.length === centrosPossiveis.length,
          handleToggleAllCentros
        )}
      </TabPanel>
    </MDBox>
  );

  // ---------------------------------------------------------------------------
  // Funções auxiliares para os Dialogs (Stocks por centro e MenuSearchMateriais)
  // ---------------------------------------------------------------------------

  const handleOpenStockDialog = (row) => {
    setStockDialogRow(row);
    setStockDialogOpen(true);
  };

  const handleCloseStockDialog = () => {
    setStockDialogOpen(false);
    setStockDialogRow(null);
  };

  const handleOpenDetalhes = (row) => {
    if (!row) return;

    setMaterialDetalhe({
      refmaterial: row.refmaterial,
      ref: row.refmaterial,
      nome: row.nomematerial,
      urlImagem: imgsByRef[String(row.refmaterial || "").trim()] || "",
    });
    setDetalhesOpen(true);
  };

  const reloadEncomendasDetalhe = async (mat) => {
    if (!mat) return;

    const payload = {
      refmaterial: mat.refmaterial,
      idutilizador: idUtilizadorAuth,
      Funcoes: userData?.funcoes || [],
    };

    const [res, err] = await handleApi(
      api.post("Materiais/SelMateriaisEncomendasDetalhe", payload)
    );
    if (err) {
      showAlert(err, "error");
      return;
    }

    // console.log("res", res);

    setEncomendasRows(Array.isArray(res) ? res : []);
  };

  const reloadPreVendasDetalhe = async (mat) => {
    if (!mat) return;
    const payload = {
      refmaterial: mat.refmaterial,
      refvendedor: refVendedorAuth,
      Funcoes: userData?.funcoes || [],
    };

    const [res, err] = await handleApi(api.post("Materiais/SelMateriaisPreVendasDetalhe", payload));
    if (err) {
      showAlert(err, "error");
      return;
    }

    console.log("res", res);

    setPreVendasRows(Array.isArray(res) ? res : []);
  };

  // ---------------------------------------------------------------------------
  // Encomendas
  // ---------------------------------------------------------------------------
  const handleOpenEncomendas = async (mat) => {
    if (!mat) return;
    if (!isAdminLike) return;

    setEncomendasMat(mat);
    setEncomendaForm({ id: 0, quantidade: "", obs: "" });
    setEncomendasRows([]);
    setEncomendasOpen(true);

    await reloadEncomendasDetalhe(mat);
  };

  const handleCloseEncomendas = () => {
    setEncomendasOpen(false);
    setEncomendasMat(null);
    setEncomendasRows([]);
    setEncomendaForm({ id: 0, quantidade: "", obs: "" });
  };

  const handleEditEncomenda = (row) => {
    if (!row) return;
    setEncomendaForm({
      id: Number(row.id || 0),
      quantidade: row.quantidade_total ?? "",
      obs: row.obs ?? "",
    });
  };

  const handleSaveEncomenda = async () => {
    if (!encomendasMat) return;

    const quantidade = Number(String(encomendaForm.quantidade || "").replace(",", "."));
    if (!quantidade || quantidade <= 0) {
      showAlert("Indica uma quantidade válida.", "warning");
      return;
    }

    let dados, err, meta;

    const refcentro = String(encomendaForm.refcentro || "").trim();
    if (!refcentro) {
      showAlert("Indica o Centro.", "warning");
      return;
    }

    // console.log("refcentro", refcentro);

    if (encomendaForm.id && encomendaForm.id > 0) {
      const payload = {
        id: encomendaForm.id,
        quantidade,
        idutilizador: idUtilizadorAuth,
        obs: encomendaForm.obs || "",
        refcentro,
        Funcoes: userData?.funcoes || [],
      };
      [dados, err, meta] = await handleApi(api.post("Materiais/UpdMaterialEncomenda", payload));
    } else {
      const payload = {
        refmaterial: encomendasMat.refmaterial,
        quantidade,
        idutilizador: idUtilizadorAuth,
        obs: encomendaForm.obs || "",
        refcentro,
        Funcoes: userData?.funcoes || [],
      };
      [dados, err, meta] = await handleApi(api.post("Materiais/InsMaterialEncomenda", payload));
    }

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Encomenda gravada.", meta?.severity || "success");

    // 1) Atualiza tabela principal (totais e dias rutura)
    const refFab = fabricanteSel?.ref;
    if (refFab) {
      await carregarAnalise(refFab);
    }

    // 2) Atualiza detalhe no dialog
    await reloadEncomendasDetalhe(encomendasMat);

    // 3) Atualiza avisos de pendentes (email)
    await carregarPendentesEmail();

    // 4) Limpa form para nova inserção
    setEncomendaForm({ id: 0, quantidade: "", obs: "" });
  };

  // ---------------------------------------------------------------------------
  // Pré-vendas (comercial + admin-like)
  // ---------------------------------------------------------------------------
  const handleOpenPreVendas = async (mat) => {
    if (!mat) return;
    if (!isComercial && !isAdminLike) return;

    setPreVendasMat(mat);
    setPreVendaForm({ id: 0, quantidade: "", obs: "" });
    setPreVendasRows([]);
    setPreVendasOpen(true);

    await reloadPreVendasDetalhe(mat);
  };

  const handleClosePreVendas = () => {
    setPreVendasOpen(false);
    setPreVendasMat(null);
    setPreVendasRows([]);
    setPreVendaForm({ id: 0, quantidade: "", obs: "" });
  };

  const handleEditPreVenda = (row) => {
    if (!row) return;
    setPreVendaForm({
      id: Number(row.id || 0),
      quantidade: row.quantidade_total ?? "",
      obs: row.obs ?? "",
    });
  };

  const handleSavePreVenda = async () => {
    if (!preVendasMat) return;

    const obs = (preVendaForm?.obs || "").trim();
    if (!obs) {
      showAlert("Observações é obrigatório (ex: Nome do cliente).", "warning");
      return;
    }

    const quantidade = Number(String(preVendaForm.quantidade || "").replace(",", "."));
    if (!quantidade || quantidade <= 0) {
      showAlert("Indica uma quantidade válida.", "warning");
      return;
    }

    let dados, err;

    // ---------------------------------------------------------
    // UPDATE
    // ---------------------------------------------------------

    const refcentro = String(preVendaForm.refcentro || "").trim();
    if (!refcentro) {
      showAlert("Indica o Centro.", "warning");
      return;
    }

    // console.log("refcentro", refcentro);

    if (preVendaForm.id && preVendaForm.id > 0) {
      // Vai buscar a linha que estamos a editar (para validar permissões)
      const linha = (preVendasRows || []).find(
        (x) => Number(x?.id || 0) === Number(preVendaForm.id || 0)
      );

      // compatível com variações de naming (por segurança)
      const dtEnvio = (linha?.data_envio_email ?? linha?.data_envio_email ?? "").toString().trim();
      const idCriador = Number(linha?.idutilizador_registo || 0);

      const podeEditar = isAdminLike || (idCriador === Number(idUtilizadorAuth || 0) && !dtEnvio);

      if (!podeEditar) {
        showAlert("Sem permissões para editar pré-vendas.", "warning");
        return;
      }

      const payload = {
        id: preVendaForm.id,
        quantidade,
        idutilizador: idUtilizadorAuth,
        obs: preVendaForm.obs || "",
        refvendedor: refVendedorAuth,
        refcentro,
        Funcoes: userData?.funcoes || [],
      };

      [dados, err] = await handleApi(api.post("Materiais/UpdMaterialPreVenda", payload));
    }
    // ---------------------------------------------------------
    // INSERT
    // ---------------------------------------------------------
    else {
      const payload = {
        refmaterial: preVendasMat.refmaterial,
        refvendedor: refVendedorAuth,
        quantidade,
        idutilizador: idUtilizadorAuth,
        obs: preVendaForm.obs || "",
        refcentro,
        Funcoes: userData?.funcoes || [],
      };

      [dados, err] = await handleApi(api.post("Materiais/InsMaterialPreVenda", payload));
    }

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(dados?.message || "Pré-venda gravada.", "success");

    // 1) Atualiza tabela principal (totais e dias rutura)
    const refFab = fabricanteSel?.ref;
    if (refFab) {
      await carregarAnalise(refFab);
    }

    // 2) Atualiza detalhe no dialog
    await reloadPreVendasDetalhe(preVendasMat);

    // 3) Atualiza avisos de pendentes (email)
    await carregarPendentesEmail();

    // 4) Limpa form
    setPreVendaForm({ id: 0, quantidade: "", obs: "" });
  };

  const handleCloseDetalhes = () => {
    setDetalhesOpen(false);
    setMaterialDetalhe(null);
  };

  const handleInativarMaterial = (row) => {
    if (!row) return;

    // se já estiver inativo, não faz nada
    if (row.inativo === 1) {
      showAlert("Este material já está inativo.", "info");
      return;
    }

    // guarda o material e abre o Dialog de confirmação
    setMaterialSelecionado(row);
    setOpenDialogInativar(true);
  };

  const handleAtivarMaterial = (row) => {
    if (!row) return;
    if (row.inativo === 0) return;

    setMaterialSelecionado(row);
    setOpenDialogAtivar(true);
  };

  const handleConfirmarAtivar = async () => {
    if (!materialSelecionado) {
      setOpenDialogAtivar(false);
      return;
    }

    setLoading(true);

    try {
      const body = {
        refmaterial: materialSelecionado.refmaterial,
        inativo: 0,
      };

      const [dados, err] = await handleApi(api.post("Materiais/UpdMaterialInativo", body));

      if (err) {
        showAlert(err, "error");
        setOpenDialogAtivar(false);
        return;
      }

      const msg = (dados && dados.message) || "Material ativado com sucesso.";
      showAlert(msg, "success");

      setRows((prev) =>
        prev.map((m) =>
          m.refmaterial === materialSelecionado.refmaterial ? { ...m, inativo: 0 } : m
        )
      );
    } catch {
      showAlert("Erro ao ativar material.", "error");
    } finally {
      setOpenDialogAtivar(false);
      setMaterialSelecionado(null);
      setLoading(false);
    }
  };

  const handleCancelarAtivar = () => {
    setOpenDialogAtivar(false);
    setMaterialSelecionado(null);
  };

  const handleConfirmarInativar = async () => {
    if (!materialSelecionado) {
      setOpenDialogInativar(false);
      return;
    }

    setLoading(true);

    try {
      const body = {
        refmaterial: materialSelecionado.refmaterial,
        inativo: 1,
      };

      const [dados, err] = await handleApi(api.post("Materiais/UpdMaterialInativo", body));

      if (err) {
        showAlert(err, "error");
        setOpenDialogInativar(false);
        return;
      }

      const msg = (dados && dados.message) || "Material inativado com sucesso.";
      showAlert(msg, "success");

      // atualizar estado local (marca este material como inativo)
      setRows((prev) =>
        prev.map((m) =>
          m.refmaterial === materialSelecionado.refmaterial ? { ...m, inativo: 1 } : m
        )
      );
    } catch {
      showAlert("Erro ao inativar material.", "error");
    } finally {
      setOpenDialogInativar(false);
      setMaterialSelecionado(null);
      setLoading(false);
    }
  };

  const handleCancelarInativar = () => {
    setOpenDialogInativar(false);
    setMaterialSelecionado(null);
  };

  // DELETE
  const handleAskDeleteEncomenda = () => {
    if (!encomendaForm?.id) return;

    const row = (encomendasRows || []).find((x) => Number(x?.id || 0) === Number(encomendaForm.id));
    if (!row) return;

    const pode =
      isAdminLike ||
      (Number(row?.idutilizador_registo || 0) === idUtilizadorAuth && !row?.data_envio_email);

    if (!pode) {
      showAlert("Sem permissões para apagar esta encomenda.", "warning");
      return;
    }

    setEncToDelete(row);
    setConfirmDelEncOpen(true);
  };

  const handleConfirmDeleteEncomenda = async () => {
    if (!encToDelete?.id) return;

    const payload = {
      id: Number(encToDelete.id),
      idutilizador: idUtilizadorAuth,
      Funcoes: userData?.funcoes || [],
    };

    const [res, err] = await handleApi(api.post("Materiais/DelMaterialEncomenda", payload));
    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(res?.message || "Encomenda apagada.", "success");
    setConfirmDelEncOpen(false);
    setEncToDelete(null);
    setEncomendaForm({ id: 0, quantidade: "", obs: "" });

    // 1) Atualiza tabela principal (Enc., Pré-vendas, Stock dispon., dias rutura)
    const refFab = fabricanteSel?.ref;
    if (refFab) {
      await carregarAnalise(refFab);
    }

    // 2) Atualiza detalhe no dialog
    await reloadEncomendasDetalhe(encomendasMat);

    // 3) Atualiza avisos de pendentes (email)
    await carregarPendentesEmail();
  };

  const handleAskDeletePreVenda = () => {
    if (!preVendaForm?.id) return;

    const row = (preVendasRows || []).find((x) => Number(x?.id || 0) === Number(preVendaForm.id));
    if (!row) return;

    const pode =
      Number(row?.is_outros || 0) !== 1 &&
      (isAdminLike ||
        (Number(row?.idutilizador_registo || 0) === idUtilizadorAuth && !row?.data_envio_email));

    if (!pode) {
      showAlert("Sem permissões para apagar esta pré-venda.", "warning");
      return;
    }

    setPreToDelete(row);
    setConfirmDelPreOpen(true);
  };

  const handleConfirmDeletePreVenda = async () => {
    if (!preToDelete?.id) return;

    const payload = {
      id: Number(preToDelete.id),
      idutilizador: idUtilizadorAuth,
      Funcoes: userData?.funcoes || [],
    };

    const [res, err] = await handleApi(api.post("Materiais/DelMaterialPreVenda", payload));
    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(res?.message || "Pré-venda apagada.", "success");
    setConfirmDelPreOpen(false);
    setPreToDelete(null);
    setPreVendaForm({ id: 0, quantidade: "", obs: "" });

    // 1) Atualiza tabela principal (Enc., Pré-vendas, Stock dispon., dias rutura)
    const refFab = fabricanteSel?.ref;
    if (refFab) {
      await carregarAnalise(refFab);
    }

    // 2) Atualiza detalhe no dialog
    await reloadPreVendasDetalhe(preVendasMat);

    // 3) Atualiza avisos de pendentes (email)
    await carregarPendentesEmail();
  };

  // ---------------------------------------------------------------------------
  // TOOLTIPs dos indicadores de "Dias de rutura"
  // ---------------------------------------------------------------------------

  const tooltipMediaAno = (
    <div style={{ fontSize: 11, maxWidth: 360 }}>
      <strong>Dias Rutura (média ano)</strong>
      <br />
      Calculado com:
      <br />
      • Vendas do material nos últimos 365 dias (até à data atual);
      <br />
      • Soma das quantidades vendidas nesse período;
      <br />
      • Consumo diário = total vendido / nº de dias do período;
      <br />• Dias de rutura = stock total atual / consumo diário.
    </div>
  );

  const tooltipMax2Meses = (
    <div style={{ fontSize: 11, maxWidth: 360 }}>
      <strong>Dias Rutura (prev. mês seg.)</strong>
      <br />• Considera a sazonalidade do <u>mês seguinte</u> ao atual;
      <br />
      • Procura o mesmo mês nos últimos 2 anos;
      <br />
      • Consumo diário = vendas máximas nesse mês / 30;
      <br />• Dias de rutura = stock total atual / consumo diário sazonal.
    </div>
  );

  const handleChangeDiasRutura = (refmaterial, value) => {
    // aceitar só dígitos (campo pequeno)
    const limpo = value.replace(/[^\d]/g, "");

    setRows((prev) =>
      prev.map((m) => (m.refmaterial === refmaterial ? { ...m, diasrutura_temp: limpo } : m))
    );
  };

  const handleBlurDiasRutura = (row) => {
    if (!row) return;

    const atual = row.diasrutura ?? 30;
    const tempStr = row.diasrutura_temp !== undefined ? row.diasrutura_temp : String(atual);

    if (tempStr === "") {
      // nada escrito: reverte para o valor original
      setRows((prev) =>
        prev.map((m) =>
          m.refmaterial === row.refmaterial ? { ...m, diasrutura_temp: undefined } : m
        )
      );
      return;
    }

    const novo = parseInt(tempStr, 10);
    if (!Number.isFinite(novo) || novo < 0) {
      // valor inválido → reverte e mostra erro
      setRows((prev) =>
        prev.map((m) =>
          m.refmaterial === row.refmaterial ? { ...m, diasrutura_temp: undefined } : m
        )
      );
      showAlert("Valor de dias de rutura inválido.", "error");
      return;
    }

    // se não mudou, apenas limpa o temp e não abre o dialog
    if (novo === atual) {
      setRows((prev) =>
        prev.map((m) =>
          m.refmaterial === row.refmaterial ? { ...m, diasrutura_temp: undefined } : m
        )
      );
      return;
    }

    // abre dialog de confirmação
    setDialogDiasRutura({
      open: true,
      refmaterial: row.refmaterial,
      nome: row.nomematerial,
      valorNovo: novo,
      valorAntigo: atual,
    });
  };

  const handleCancelarDiasRutura = () => {
    setRows((prev) =>
      prev.map((m) =>
        m.refmaterial === dialogDiasRutura.refmaterial ? { ...m, diasrutura_temp: undefined } : m
      )
    );
    setDialogDiasRutura({
      open: false,
      refmaterial: null,
      nome: "",
      valorNovo: null,
      valorAntigo: null,
    });
  };

  const handleConfirmarDiasRutura = async () => {
    const { refmaterial, valorNovo } = dialogDiasRutura;

    if (!refmaterial || valorNovo === null || valorNovo === undefined || Number.isNaN(valorNovo)) {
      handleCancelarDiasRutura();
      return;
    }

    try {
      const body = { refmaterial, diasrutura: valorNovo };
      const [dados, err] = await handleApi(api.post("Materiais/UpdMaterialDiasRutura", body));

      if (err) {
        showAlert(err, "error");
        handleCancelarDiasRutura();
        return;
      }

      const msg = (dados && dados.message) || "Dias de rutura atualizados com sucesso.";
      showAlert(msg, "success");

      setRows((prev) =>
        prev.map((m) =>
          m.refmaterial === refmaterial
            ? { ...m, diasrutura: valorNovo, diasrutura_temp: undefined }
            : m
        )
      );
    } catch {
      showAlert("Erro ao atualizar dias de rutura.", "error");
    } finally {
      setDialogDiasRutura({
        open: false,
        refmaterial: null,
        nome: "",
        valorNovo: null,
        valorAntigo: null,
      });
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "",
        id: "detalhes",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row }) => {
          const mat = row.original;
          const valorCampo =
            mat.diasrutura_temp !== undefined ? mat.diasrutura_temp : mat.diasrutura ?? 30;

          return (
            <MDBox display="flex" gap={0.5} alignItems="center">
              {isAdminLike && (
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => handleOpenDetalhes(mat)}
                  title="Info"
                  //sx={{ border: "1px solid", borderColor: "info.main", borderRadius: 1 }}
                >
                  <InfoIcon fontSize="inherit" />
                </IconButton>
              )}

              {isAdminLike && (
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => handleOpenEncomendas(mat)}
                  title="Encomendas"
                  //sx={{ border: "1px solid", borderColor: "warning.main", borderRadius: 1 }}
                >
                  <LocalShippingOutlinedIcon fontSize="inherit" />
                </IconButton>
              )}

              {(isComercial || isAdminLike) && (
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleOpenPreVendas(mat)}
                  title="Pré-vendas"
                  //sx={{ border: "1px solid", borderColor: "success.main", borderRadius: 1 }}
                >
                  <SellOutlinedIcon fontSize="inherit" />
                </IconButton>
              )}

              {isAdminLike &&
                (mat.inativo === 1 ? (
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleAtivarMaterial(mat)}
                    title="Ativar material"
                    // sx={{
                    //   border: "1px solid #2e7d32",
                    //   borderColor: "success.main",
                    //   borderRadius: 1,
                    // }}
                  >
                    <CheckCircleOutlineIcon fontSize="inherit" />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleInativarMaterial(mat)}
                    title="Inativar material"
                    // sx={{
                    //   border: "1px solid #d32f2f",
                    //   borderColor: "error.main",
                    //   borderRadius: 1,
                    // }}
                  >
                    <BlockIcon fontSize="inherit" />
                  </IconButton>
                ))}

              {/* Dias Rutura — editável (apenas admin-like) */}
              {isAdminLike && (
                <TextField
                  value={valorCampo}
                  onChange={(e) => handleChangeDiasRutura(mat.refmaterial, e.target.value)}
                  onBlur={() => handleBlurDiasRutura(mat)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleBlurDiasRutura(mat);
                    }
                  }}
                  size="small"
                  sx={{
                    width: 48,
                    "& .MuiInputBase-input": {
                      fontSize: 11,
                      padding: "2px 4px",
                      textAlign: "center",
                    },
                  }}
                />
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "",
        id: "img",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row }) => {
          const ref = String(row.original.refmaterial || "").trim();
          const url = ref ? imgsByRef[ref] : "";

          if (!url) {
            return <span style={{ display: "inline-block", width: 36 }} />;
          }

          return (
            <img
              src={url}
              alt={ref}
              style={{
                width: 32,
                height: 32,
                objectFit: "contain",
                borderRadius: 6,
                cursor: "pointer",
                border: "1px solid #eee",
                background: "#fff",
              }}
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
              title="Abrir imagem"
            />
          );
        },
      },
      {
        Header: "Refª Material",
        accessor: "refmaterial",
      },
      {
        Header: "Nome Material",
        accessor: "nomematerial",
      },
      {
        Header: "Qtd Stk",
        accessor: "qtdstocktotal",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ row }) => {
          const original = row.original;
          const unidade = original.unidade || "";
          const stocksCentros = original.stocks_centros || [];

          // Centros considerados: se não houver nada selecionado,
          // assume que todos os centros do registo estão “ativos”.
          const totalSelecionado = Number(original.qtdstocktotal || 0);

          const tooltipContent =
            stocksCentros.length === 0 ? (
              <div style={{ fontSize: 11 }}>Sem detalhe por centro.</div>
            ) : (
              <div style={{ fontSize: 11 }}>
                {stocksCentros.map((s, idx) => (
                  <div key={idx}>
                    {s.centro} —{" "}
                    {`${Number(s.quantidade || 0).toLocaleString("pt-PT")} ${s.unidade || ""}`}
                  </div>
                ))}
              </div>
            );

          const label = `${Number(totalSelecionado).toLocaleString("pt-PT")} ${unidade || ""}`;

          return (
            <Tooltip title={tooltipContent} arrow>
              <span
                onClick={() => handleOpenStockDialog(original)}
                style={{
                  cursor: "pointer",
                  textDecoration: "underline dotted",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </span>
            </Tooltip>
          );
        },
      },

      {
        Header: "Enc.",
        accessor: "qtdencomendas",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ row }) => {
          const m = row.original;
          return m.qtdencomendas ?? 0;
        },
      },
      {
        Header: "Pré-vendas",
        accessor: "qtdprevendas",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ row }) => {
          const m = row.original;
          return m.qtdprevendas ?? 0;
        },
      },
      {
        Header: <span style={{ whiteSpace: "normal", lineHeight: 1.1 }}>Stock Dispon.</span>,
        accessor: "qtdstockdisponivel",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ row }) => {
          const m = row.original;
          return m.qtdstockdisponivel ?? 0;
        },
      },
      {
        Header: (
          <span
            style={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
              display: "inline-block",
            }}
          >
            Consumo diário
            <br />
            (média ano)
          </span>
        ),
        accessor: "consumo_diario_media_ano",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ row }) => {
          const m = row.original;

          const valor = m.consumo_diario_media_ano;
          if (valor == null) return "-";

          const num = Number(valor);
          const unidade = m.unidade || "";
          const sufixo = unidade ? `${unidade}` : "un";

          return (
            <span style={{ whiteSpace: "nowrap" }}>
              {num.toLocaleString("pt-PT", { maximumFractionDigits: 3 })} {sufixo}
            </span>
          );
        },
      },
      {
        Header: () => (
          <span
            style={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              lineHeight: 1.2,
              whiteSpace: "normal",
            }}
          >
            Dias Rutura
            <span>(média ano)</span>
            <Tooltip title={tooltipMediaAno} arrow>
              <InfoIcon sx={{ color: "info", fontSize: 16, cursor: "pointer", marginTop: 2 }} />
            </Tooltip>
          </span>
        ),
        accessor: "dias_rutura_media_ano",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ row }) => {
          const m = row.original;

          const value = m.dias_rutura_media_ano;

          if (value == null) return "-";

          const num = Number(value);
          const limiar = m.diasrutura ?? 30; // usa diasrutura do material
          const critico = num < limiar;

          return (
            <span
              style={{
                color: critico ? "#f44335" : "inherit",
                fontWeight: critico ? 600 : 400,
                whiteSpace: "nowrap",
              }}
            >
              {num.toLocaleString("pt-PT", { maximumFractionDigits: 1 })} dias
            </span>
          );
        },
      },

      {
        Header: (
          <span
            style={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
              display: "inline-block",
            }}
          >
            Consumo diário
            <br />
            (mês seg.)
          </span>
        ),
        accessor: "consumo_diario_mes_seg",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ row }) => {
          const m = row.original;

          const valor = m.consumo_diario_mes_seg;

          if (valor == null) return "-";

          const num = Number(valor);
          const unidade = m.unidade || "";
          const sufixo = unidade ? `${unidade}` : "un";

          return (
            <span style={{ whiteSpace: "nowrap" }}>
              {num.toLocaleString("pt-PT", { maximumFractionDigits: 3 })} {sufixo}
            </span>
          );
        },
      },
      {
        Header: () => (
          <span
            style={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              lineHeight: 1.2,
              whiteSpace: "normal",
            }}
          >
            Dias Rutura
            <span>(máx. mês seg.)</span>
            <Tooltip title={tooltipMax2Meses} arrow>
              <InfoIcon sx={{ color: "info", fontSize: 16, cursor: "pointer", marginTop: 2 }} />
            </Tooltip>
          </span>
        ),
        accessor: "dias_rutura_max_2meses",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ row }) => {
          const m = row.original;

          const value = m.dias_rutura_max_2meses;

          if (value == null) return "-";

          const num = Number(value);
          const limiar = m.diasrutura ?? 30;
          const critico = num < limiar;

          return (
            <span
              style={{
                color: critico ? "#f44335" : "inherit",
                fontWeight: critico ? 600 : 400,
                whiteSpace: "nowrap",
              }}
            >
              {num.toLocaleString("pt-PT", { maximumFractionDigits: 1 })} dias
            </span>
          );
        },
      },
    ];
  }, [imgsByRef, isAdminLike, isComercial]);

  // ---------------------------------------------------------------------------
  // INTEGRAÇÃO DAS TABS NO LAYOUT PRINCIPAL (Card)
  // ---------------------------------------------------------------------------

  const filtrosTopo = (
    <MDBox
      p={2}
      display="flex"
      flexWrap="wrap"
      gap={2}
      alignItems="center"
      justifyContent="space-between"
    >
      <MDBox display="flex" alignItems="center" gap={2}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={mostrarInativos}
              onChange={(e) => setMostrarInativos(e.target.checked)}
              color="info"
            />
          }
          label={<span style={{ fontSize: 12 }}>Mostrar materiais inativos</span>}
          sx={{ m: 0 }}
        />
      </MDBox>

      <MDBox flex={1} minWidth={260}>
        <MDTypography
          variant="button"
          color="text"
          sx={{ fontSize: 12, textTransform: "none", mb: 0.5 }}
        >
          Fabricante
        </MDTypography>

        <Autocomplete
          size="small"
          options={fabricantes}
          getOptionLabel={(opt) =>
            opt ? `${opt.ref || ""} — ${opt.nome || opt.designacao || ""}` : ""
          }
          value={fabricanteSel}
          onChange={handleChangeFabricante}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Selecione o fabricante..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: 13,
                },
              }}
            />
          )}
          isOptionEqualToValue={(opt, val) => String(opt.ref) === String(val.ref)}
          noOptionsText="Sem resultados"
        />
      </MDBox>
    </MDBox>
  );

  const pendentesColumns = useMemo(
    () => [
      {
        Header: "Fabricante",
        accessor: "fabricante",
      },
      {
        Header: "Refª Material",
        accessor: "refmaterial",
      },
      {
        Header: "Nome Material",
        accessor: "nomematerial",
      },
      {
        Header: () => (
          <span style={{ whiteSpace: "normal", lineHeight: "1.2" }}>
            Pré-vendas
            <br />
            Qtd. Pend.
          </span>
        ),
        accessor: "prevendas_pendentes",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ value }) => Number(value || 0).toLocaleString("pt-PT"),
      },
      {
        Header: () => (
          <span style={{ whiteSpace: "normal", lineHeight: "1.2" }}>
            Encom.
            <br />
            Qtd. Pend.
          </span>
        ),
        accessor: "encomendas_pendentes",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ value }) => Number(value || 0).toLocaleString("pt-PT"),
      },
      {
        Header: "PC",
        accessor: "preco_custo",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ value }) =>
          `${Number(value || 0).toLocaleString("pt-PT", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} €`,
      },
      {
        Header: "Valor",
        accessor: "valor_encomenda",
        disableFilters: true,
        sortType: "numPt",
        Cell: ({ value }) =>
          `${Number(value || 0).toLocaleString("pt-PT", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} €`,
      },
      {
        Header: "Data Encomenda",
        accessor: "encomendas_formatadas",
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row }) => {
          const lista = Array.isArray(row.original?.encomendas_lista)
            ? row.original.encomendas_lista
            : [];

          if (lista.length === 0) return "";

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, lineHeight: "1.35" }}>
              {lista.map((enc, idx) => (
                <div key={`${enc.idencomenda || idx}`}>
                  {enc.data} ({Number(enc.quantidade_total || 0).toLocaleString("pt-PT")})
                </div>
              ))}
            </div>
          );
        },
      },
    ],
    []
  );

  const pendentesRowsAgrupadas = useMemo(() => {
    const mapa = new Map();

    (Array.isArray(pendentesRows) ? pendentesRows : []).forEach((r) => {
      const fabricante = String(r?.fabricante || "").trim();
      const refmaterial = String(r?.refmaterial || "").trim();
      const nomematerial = String(r?.nomematerial || "").trim();

      const chave = `${fabricante}|||${refmaterial}|||${nomematerial}`;

      if (!mapa.has(chave)) {
        const precoCusto = Number(r?.preco_custo || 0);
        const qtdEncomendas = Number(r?.encomendas_pendentes || 0);

        mapa.set(chave, {
          fabricante,
          refmaterial,
          nomematerial,
          prevendas_pendentes: Number(r?.prevendas_pendentes || 0),
          encomendas_pendentes: qtdEncomendas,
          preco_custo: precoCusto,
          valor_encomenda: qtdEncomendas * precoCusto,
          encomendas_lista: [],
        });
      }

      const grupo = mapa.get(chave);

      let dataFmt = "";
      if (r?.data_encomenda) {
        const dt = new Date(r.data_encomenda);
        dataFmt = Number.isNaN(dt.getTime())
          ? String(r.data_encomenda)
          : dt.toLocaleDateString("pt-PT");
      }

      grupo.encomendas_lista.push({
        idencomenda: r?.idencomenda ?? null,
        data: dataFmt,
        quantidade_total: Number(r?.encomenda_quantidade_total || 0),
      });
    });

    return Array.from(mapa.values()).map((item) => ({
      ...item,
      encomendas_lista: item.encomendas_lista.sort((a, b) => {
        const da = a.data || "";
        const db = b.data || "";
        if (da !== db) return da.localeCompare(db, "pt");
        return Number(a.idencomenda || 0) - Number(b.idencomenda || 0);
      }),
      encomendas_formatadas: "",
    }));
  }, [pendentesRows]);

  const pendentesTotaisPorFabricante = useMemo(() => {
    const mapa = new Map();

    (Array.isArray(pendentesRowsAgrupadas) ? pendentesRowsAgrupadas : []).forEach((r) => {
      const fabricante = String(r?.fabricante || "").trim() || "Sem fabricante";
      const valor = Number(r?.valor_encomenda || 0);

      mapa.set(fabricante, (mapa.get(fabricante) || 0) + valor);
    });

    const linhas = Array.from(mapa.entries())
      .map(([fabricante, valor]) => ({ fabricante, valor }))
      .sort((a, b) => a.fabricante.localeCompare(b.fabricante, "pt-PT"));

    const totalGeral = linhas.reduce((acc, x) => acc + Number(x.valor || 0), 0);

    return { linhas, totalGeral };
  }, [pendentesRowsAgrupadas]);

  const handleEnviarEncomendasPendentes = async () => {
    const [dados, err, meta] = await handleApi(
      api.post("Materiais/EnviarEmailEncomendasPendentes", {})
    );

    console.log("dados", dados);
    console.log("err", err);
    console.log("meta", meta);

    if (err) return;

    if (meta?.success) {
      showAlert(meta?.message || "Email enviado.", "success");

      carregarPendentesEmail();
    } else {
      showAlert(meta?.message || "Erro ao enviar email.", "error");
    }
  };

  const handleEnviarPreVendasPendentes = async () => {
    console.log("refVendedorAuth", refVendedorAuth);

    const [dados, err, meta] = await handleApi(
      api.post("Materiais/EnviarEmailPreVendasPendentes", { refvendedor: refVendedorAuth })
    );

    console.log("meta", meta);

    if (err) return;

    if (meta?.success) {
      showAlert(meta?.message || "Email enviado.", "success");

      carregarPendentesEmail();
    } else {
      showAlert(meta?.message || "Erro ao enviar email.", "error");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <PageHeader />

      {(pendEmailEnc > 0 || pendEmailPre > 0) && (
        <MDBox mb={2}>
          {pendEmailEnc > 0 && (
            <MDBox
              p={1.5}
              mb={1}
              borderRadius={2}
              sx={{ border: "1px solid #ffe0b2", background: "#fff8e1" }}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <MDTypography variant="button" color="text">
                Existem <b>{pendEmailEnc}</b> encomendas pendentes de envio por e-mail.
              </MDTypography>
              <MDButton color="warning" size="small" onClick={handleEnviarEncomendasPendentes}>
                Enviar encomendas pendentes
              </MDButton>
            </MDBox>
          )}

          {pendEmailPre > 0 && (
            <MDBox
              p={1.5}
              borderRadius={2}
              sx={{ border: "1px solid #bbdefb", background: "#e3f2fd" }}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <MDTypography variant="button" color="text">
                Existem <b>{pendEmailPre}</b> pré-vendas pendentes de envio por e-mail.
              </MDTypography>
              <MDButton color="info" size="small" onClick={handleEnviarPreVendasPendentes}>
                Enviar pré-vendas pendentes
              </MDButton>
            </MDBox>
          )}
        </MDBox>
      )}

      {/* PROGRESS GLOBAL */}
      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <MDBox pt={1}>
        <Card>
          {/* Linha de filtros */}
          {filtrosTopo}

          {/* Tabs (Tipos / Grupos / Famílias / Segmentos / Marcas) */}
          {tabsSecao}

          {/* Tabela */}
          <MDBox px={2} pb={2}>
            <MDBox display="flex" gap={1} mb={1.5} flexWrap="wrap">
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleRecalcular}
                disabled={!fabricanteSel || loading || !analisePronta}
              >
                Recalcular
              </MDButton>

              <MDButton
                variant="outlined"
                color="info"
                onClick={handleOpenPendentes}
                disabled={loading || (!!fabricanteSel && !analisePronta)}
              >
                Ver Pendentes
              </MDButton>
            </MDBox>
            <ProjectTable
              tableKey="analise-stocks"
              version="v1"
              columns={columns}
              data={rows}
              loading={false}
              emptyMessage="Sem materiais para apresentar."
              minWidth={1500}
              maxHeight="68vh"
              showKeepFilters
            />
          </MDBox>
        </Card>
      </MDBox>

      <Dialog
        open={pendentesOpen}
        onClose={handleClosePendentes}
        maxWidth="lg"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
          Pendentes de materiais
          {fabricanteSel?.nome && !pendentesVerTodos
            ? ` — ${fabricanteSel.ref} — ${fabricanteSel.nome}`
            : ""}
        </DialogTitle>

        <DialogContent dividers>
          <MDBox mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={pendentesVerTodos}
                  onChange={handleToggleVerTodosPendentes}
                  color="primary"
                  disabled={!fabricanteSel?.ref}
                />
              }
              label="Ver todos"
            />
          </MDBox>

          {pendentesLoading ? (
            <MDBox py={3} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </MDBox>
          ) : (
            <MDBox>
              <ProjectTable
                tableKey="analise-stocks-pendentes"
                version="v1"
                columns={pendentesColumns}
                data={pendentesRowsAgrupadas}
                loading={false}
                emptyMessage="Sem materiais pendentes."
                minWidth={1000}
                maxHeight="46vh"
                defaultSortBy={[
                  {
                    id: "fabricante",
                    desc: false,
                  },
                  {
                    id: "nomematerial",
                    desc: false,
                  },
                ]}
                showKeepFilters
              />

              {pendentesTotaisPorFabricante.linhas.length > 0 && (
                <MDBox
                  mt={2}
                  p={1.5}
                  sx={{
                    border: "1px solid #eee",
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                    minWidth: 360,
                  }}
                >
                  <MDTypography variant="button" fontWeight="bold" sx={{ fontSize: 13 }}>
                    Totais por fabricante
                  </MDTypography>

                  <MDBox mt={1}>
                    {pendentesTotaisPorFabricante.linhas.map((x) => (
                      <MDBox
                        key={x.fabricante}
                        display="flex"
                        justifyContent="space-between"
                        gap={2}
                        sx={{ fontSize: 12, borderBottom: "1px solid #eee", py: 0.5 }}
                      >
                        <span>{x.fabricante}</span>
                        <strong>
                          {Number(x.valor || 0).toLocaleString("pt-PT", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{" "}
                          €
                        </strong>
                      </MDBox>
                    ))}

                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      gap={2}
                      sx={{ fontSize: 13, pt: 1 }}
                    >
                      <strong>Total geral</strong>
                      <strong>
                        {Number(pendentesTotaisPorFabricante.totalGeral || 0).toLocaleString(
                          "pt-PT",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}{" "}
                        €
                      </strong>
                    </MDBox>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton onClick={handleClosePendentes} color="secondary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog — Stocks por centro */}
      <Dialog open={stockDialogOpen} onClose={handleCloseStockDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Stocks por centro</DialogTitle>
        <DialogContent dividers>
          {stockDialogRow ? (
            <MDBox>
              <MDTypography variant="button" sx={{ fontSize: 13, mb: 1 }}>
                {stockDialogRow.refmaterial} — {stockDialogRow.nomematerial}
              </MDTypography>

              <MDBox mt={2}>
                {(!stockDialogRow.stocks_centros || stockDialogRow.stocks_centros.length === 0) && (
                  <MDTypography variant="button" sx={{ fontSize: 12, color: "#9e9e9e" }}>
                    Sem detalhe por centro.
                  </MDTypography>
                )}

                {stockDialogRow.stocks_centros && stockDialogRow.stocks_centros.length > 0 && (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "6px 4px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          Centro
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "6px 4px",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          Quantidade
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockDialogRow.stocks_centros.map((s, idx) => (
                        <tr key={idx}>
                          <td
                            style={{
                              padding: "4px 4px",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            {s.centro}
                          </td>
                          <td
                            style={{
                              padding: "4px 4px",
                              borderBottom: "1px solid #f0f0f0",
                              textAlign: "right",
                            }}
                          >
                            {`${Number(s.quantidade || 0).toLocaleString("pt-PT")} ${
                              s.unidade || ""
                            }`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </MDBox>
            </MDBox>
          ) : null}
        </DialogContent>

        <DialogActions>
          <MDButton onClick={handleCloseStockDialog} color="secondary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog — Confirmar inativação do material */}
      <Dialog open={openDialogInativar} onClose={handleCancelarInativar} maxWidth="xs" fullWidth>
        <DialogTitle>Inativar material</DialogTitle>
        <DialogContent dividers>
          {materialSelecionado && (
            <MDBox>
              <MDTypography variant="body2" mb={1}>
                Tem a certeza que pretende inativar o material:
              </MDTypography>

              <MDTypography fontWeight="bold">{materialSelecionado.refmaterial}</MDTypography>
              <MDTypography fontWeight="bold" mb={2}>
                {materialSelecionado.nomematerial}
              </MDTypography>

              <MDTypography variant="body2" color="error">
                Esta ação não pode ser anulada.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelarInativar} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmarInativar} color="error" variant="contained">
            Inativar
          </MDButton>
        </DialogActions>
      </Dialog>
      {/* Dialog — Encomendas do material (admin-like) */}
      <Dialog
        open={encomendasOpen}
        onClose={handleCloseEncomendas}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
          Encomendas — {encomendasMat?.nomematerial || encomendasMat?.refmaterial || ""}
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
            {/* Centro */}
            <MDBox display="flex" flexDirection="column" sx={{ minWidth: 220 }}>
              <MDTypography variant="caption" fontWeight="medium" mb={0.5}>
                Centro
              </MDTypography>

              <select
                value={encomendaForm.refcentro || ""}
                onChange={(e) =>
                  setEncomendaForm((prev) => ({ ...prev, refcentro: e.target.value }))
                }
                style={{
                  height: 40,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid #d0d0d0",
                  fontSize: 14,
                  backgroundColor: "white",
                  width: "100%",
                }}
              >
                <option value="">(Selecionar)</option>
                {(centrosPossiveis || []).map(([ref, nome]) => (
                  <option key={ref} value={ref}>
                    {nome} ({ref})
                  </option>
                ))}
              </select>
            </MDBox>

            {/* Quantidade */}
            <MDBox display="flex" flexDirection="column" sx={{ minWidth: 140 }}>
              <MDTypography variant="caption" fontWeight="medium" mb={0.5}>
                Quantidade
              </MDTypography>

              <TextField
                value={encomendaForm.quantidade}
                onChange={(e) =>
                  setEncomendaForm((prev) => ({ ...prev, quantidade: e.target.value }))
                }
                size="small"
              />
            </MDBox>

            {/* Observações */}
            <MDBox display="flex" flexDirection="column" sx={{ minWidth: 260, flexGrow: 1 }}>
              <MDTypography variant="caption" fontWeight="medium" mb={0.5}>
                Observações
              </MDTypography>

              <TextField
                value={encomendaForm.obs}
                onChange={(e) =>
                  setEncomendaForm((s) => ({ ...s, obs: (e.target.value || "").toUpperCase() }))
                }
                size="small"
                fullWidth
              />
            </MDBox>

            {/* Botões */}
            <MDBox display="flex" flexDirection="column" sx={{ gap: 1 }}>
              {/* “label” fantasma para alinhar com os campos */}
              <MDTypography
                variant="caption"
                fontWeight="medium"
                mb={0.5}
                sx={{ visibility: "hidden" }}
              >
                _
              </MDTypography>

              <MDBox display="flex" gap={1} flexWrap="wrap">
                {encomendaForm.id ? (
                  <MDButton color="error" variant="outlined" onClick={handleAskDeleteEncomenda}>
                    Apagar
                  </MDButton>
                ) : null}

                <MDButton
                  color="info"
                  variant="contained"
                  onClick={handleSaveEncomenda}
                  startIcon={encomendaForm.id ? <EditOutlinedIcon /> : <AddCircleOutlineIcon />}
                >
                  {encomendaForm.id ? "Atualizar" : "Adicionar"}
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Lista */}
          <MDBox sx={{ overflowX: "auto" }}>
            <MDBox
              display="flex"
              px={1}
              py={1}
              sx={{
                borderBottom: "1px solid #eee",
                fontWeight: 600,
                minWidth: 980,
                whiteSpace: "nowrap",
              }}
            >
              <MDBox sx={{ width: 180, flexShrink: 0 }}>Centro</MDBox>
              <MDBox sx={{ width: 110, flexShrink: 0 }}>Data</MDBox>
              <MDBox sx={{ width: 120, textAlign: "right", flexShrink: 0 }}>Total</MDBox>
              <MDBox sx={{ width: 140, textAlign: "right", flexShrink: 0 }}>Pendente</MDBox>
              <MDBox sx={{ minWidth: 300, pl: 2, flexShrink: 0 }}>Obs</MDBox>
              <MDBox sx={{ width: 50, flexShrink: 0 }} />
            </MDBox>

            {encomendasRows?.filter((r) => Number(r?.quantidade_pendente ?? 0) > 0)?.length ? (
              encomendasRows
                .filter((r) => Number(r?.quantidade_pendente ?? 0) > 0)
                .map((r, idx) => (
                  <MDBox
                    key={r?.id || idx}
                    display="flex"
                    px={1}
                    py={0.75}
                    alignItems="center"
                    sx={{ borderBottom: "1px solid #f2f2f2", minWidth: 980, whiteSpace: "nowrap" }}
                  >
                    <MDBox sx={{ width: 180, flexShrink: 0, fontSize: 12 }}>
                      {r?.centro || r?.refcentro || ""}
                    </MDBox>
                    <MDBox sx={{ width: 110, flexShrink: 0, fontSize: 12 }}>
                      {r?.data_registo ? String(r.data_registo).split("T")[0] : ""}
                    </MDBox>
                    <MDBox sx={{ width: 120, flexShrink: 0, textAlign: "right", fontSize: 12 }}>
                      {r?.quantidade_total ?? 0}
                    </MDBox>
                    <MDBox sx={{ width: 140, flexShrink: 0, textAlign: "right", fontSize: 12 }}>
                      {r?.quantidade_pendente ?? 0}
                    </MDBox>
                    <MDBox
                      sx={{
                        minWidth: 300,
                        pl: 2,
                        flexShrink: 0,
                        fontSize: 12,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r?.obs || ""}
                    </MDBox>
                    <MDBox sx={{ width: 50, flexShrink: 0, textAlign: "right" }}>
                      {(() => {
                        const podeEditarOuApagar =
                          isAdminLike ||
                          (Number(r?.idutilizador_registo || 0) === idUtilizadorAuth &&
                            !r?.data_envio_email);

                        if (!podeEditarOuApagar) return null;

                        return (
                          <IconButton
                            size="small"
                            onClick={() => handleEditEncomenda(r)}
                            title="Editar"
                          >
                            <EditOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        );
                      })()}
                    </MDBox>
                  </MDBox>
                ))
            ) : (
              <MDTypography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
                Sem encomendas registadas.
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={handleCloseEncomendas} color="secondary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog — Pré-vendas do material (Comercial + admin-like) */}
      <Dialog
        open={preVendasOpen}
        onClose={handleClosePreVendas}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
          Pré-vendas — {preVendasMat?.nomematerial || preVendasMat?.refmaterial || ""}
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
            {/* Centro */}
            <MDBox display="flex" flexDirection="column" sx={{ minWidth: 220 }}>
              <MDTypography variant="caption" fontWeight="medium" mb={0.5}>
                Centro
              </MDTypography>

              <select
                value={preVendaForm.refcentro || ""}
                onChange={(e) =>
                  setPreVendaForm((prev) => ({ ...prev, refcentro: e.target.value }))
                }
                style={{
                  height: 40,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid #d0d0d0",
                  fontSize: 14,
                  backgroundColor: "white",
                  width: "100%",
                }}
              >
                <option value="">(Selecionar)</option>
                {(centrosPossiveis || []).map(([ref, nome]) => (
                  <option key={ref} value={ref}>
                    {nome} ({ref})
                  </option>
                ))}
              </select>
            </MDBox>

            {/* Quantidade */}
            <MDBox display="flex" flexDirection="column" sx={{ minWidth: 140 }}>
              <MDTypography variant="caption" fontWeight="medium" mb={0.5}>
                Quantidade
              </MDTypography>

              <TextField
                value={preVendaForm.quantidade}
                onChange={(e) => setPreVendaForm((s) => ({ ...s, quantidade: e.target.value }))}
                size="small"
              />
            </MDBox>

            {/* Observações */}
            <MDBox display="flex" flexDirection="column" sx={{ minWidth: 260, flexGrow: 1 }}>
              <MDTypography variant="caption" fontWeight="medium" mb={0.5}>
                Observações
              </MDTypography>

              <TextField
                value={preVendaForm.obs}
                placeholder="NOME DO CLIENTE"
                onChange={(e) =>
                  setPreVendaForm((s) => ({ ...s, obs: (e.target.value || "").toUpperCase() }))
                }
                size="small"
                fullWidth
              />
            </MDBox>

            {/* Botões */}
            <MDBox display="flex" flexDirection="column" sx={{ gap: 1 }}>
              <MDTypography
                variant="caption"
                fontWeight="medium"
                mb={0.5}
                sx={{ visibility: "hidden" }}
              >
                _
              </MDTypography>

              <MDBox display="flex" gap={1} flexWrap="wrap">
                {preVendaForm.id ? (
                  <MDButton color="error" variant="outlined" onClick={handleAskDeletePreVenda}>
                    Apagar
                  </MDButton>
                ) : null}

                <MDButton
                  color="info"
                  variant="contained"
                  onClick={handleSavePreVenda}
                  startIcon={preVendaForm.id ? <EditOutlinedIcon /> : <AddCircleOutlineIcon />}
                >
                  {preVendaForm.id ? "Atualizar" : "Adicionar"}
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Lista */}
          <MDBox>
            <MDBox
              display="flex"
              px={1}
              py={1}
              sx={{
                borderBottom: "1px solid #eee",
                fontWeight: 600,
                minWidth: 1120,
                whiteSpace: "nowrap",
              }}
            >
              <MDBox sx={{ width: 150, flexShrink: 0 }}>Centro</MDBox>
              <MDBox sx={{ width: 180, flexShrink: 0 }}>Vendedor</MDBox>
              <MDBox sx={{ width: 110, flexShrink: 0 }}>Data</MDBox>
              <MDBox sx={{ width: 120, textAlign: "right", flexShrink: 0 }}>Total</MDBox>
              <MDBox sx={{ width: 140, textAlign: "right", flexShrink: 0 }}>Pendente</MDBox>
              <MDBox sx={{ minWidth: 300, pl: 2, flexShrink: 0 }}>Obs</MDBox>
              <MDBox sx={{ width: 50, flexShrink: 0 }} />
            </MDBox>

            {preVendasRows?.filter((r) => Number(r?.quantidade_pendente ?? 0) > 0)?.length ? (
              preVendasRows
                .filter((r) => Number(r?.quantidade_pendente ?? 0) > 0)
                .map((r, idx) => {
                  const isOutros = Number(r?.is_outros || 0) === 1;

                  return (
                    <MDBox
                      key={r?.id || (isOutros ? "outros" : idx)}
                      display="flex"
                      px={1}
                      py={0.75}
                      alignItems="center"
                      sx={{
                        borderBottom: "1px solid #f2f2f2",
                        minWidth: 1120,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <MDBox
                        sx={{
                          width: 150,
                          flexShrink: 0,
                          fontSize: 12,
                          fontWeight: isOutros ? 700 : 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={r?.centro || r?.refcentro || ""}
                      >
                        {r?.centro || r?.refcentro || ""}
                      </MDBox>
                      <MDBox
                        sx={{
                          width: 180,
                          flexShrink: 0,
                          fontSize: 12,
                          fontWeight: isOutros ? 700 : 400,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={r?.nomevendedor || r?.refvendedor || ""}
                      >
                        {r?.nomevendedor || r?.refvendedor || ""}
                      </MDBox>
                      <MDBox
                        sx={{
                          width: 110,
                          flexShrink: 0,
                          fontSize: 12,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {r?.data_registo ? String(r.data_registo).split("T")[0] : ""}
                      </MDBox>
                      <MDBox sx={{ width: 120, flexShrink: 0, textAlign: "right", fontSize: 12 }}>
                        {r?.quantidade_total ?? 0}
                      </MDBox>
                      <MDBox sx={{ width: 140, flexShrink: 0, textAlign: "right", fontSize: 12 }}>
                        {r?.quantidade_pendente ?? 0}
                      </MDBox>
                      <MDBox
                        sx={{
                          minWidth: 300,
                          pl: 2,
                          flexShrink: 0,
                          fontSize: 12,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r?.obs || ""}
                      </MDBox>
                      <MDBox sx={{ width: 50, flexShrink: 0, textAlign: "right" }}>
                        {(() => {
                          const podeEditarOuApagar =
                            !isOutros &&
                            (isAdminLike ||
                              (Number(r?.idutilizador_registo || 0) === idUtilizadorAuth &&
                                !r?.data_envio_email));

                          if (!podeEditarOuApagar) return null;

                          return (
                            <IconButton
                              size="small"
                              onClick={() => handleEditPreVenda(r)}
                              title="Editar"
                            >
                              <EditOutlinedIcon fontSize="inherit" />
                            </IconButton>
                          );
                        })()}
                      </MDBox>
                    </MDBox>
                  );
                })
            ) : (
              <MDTypography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
                Sem pré-vendas registadas.
              </MDTypography>
            )}

            {!isAdminLike && (
              <MDTypography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.75 }}>
                Nota: em detalhe, apenas vês as tuas pré-vendas. As dos outros vendedores surgem
                agregadas.
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={handleClosePreVendas} color="secondary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog — Confirmar ativação */}
      <Dialog open={openDialogAtivar} onClose={handleCancelarAtivar} maxWidth="xs" fullWidth>
        <DialogTitle>Ativar material</DialogTitle>
        <DialogContent dividers>
          {materialSelecionado && (
            <MDBox>
              <MDTypography variant="body2" mb={1}>
                Tem a certeza que pretende ativar o material:
              </MDTypography>

              <MDTypography fontWeight="bold">{materialSelecionado.refmaterial}</MDTypography>
              <MDTypography fontWeight="bold" mb={2}>
                {materialSelecionado.nomematerial}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelarAtivar} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmarAtivar} color="info" variant="contained">
            Ativar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog — Confirmar alteração de dias de rutura */}
      <Dialog
        open={dialogDiasRutura.open}
        onClose={handleCancelarDiasRutura}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleConfirmarDiasRutura();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            handleCancelarDiasRutura();
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Alterar dias de rutura</DialogTitle>
        <DialogContent dividers>
          {dialogDiasRutura.refmaterial && (
            <MDBox>
              <MDTypography variant="body2" mb={1}>
                Pretende alterar os dias de rutura do material:
              </MDTypography>
              <MDTypography fontWeight="bold">{dialogDiasRutura.refmaterial}</MDTypography>
              <MDTypography fontWeight="bold" mb={2}>
                {dialogDiasRutura.nome}
              </MDTypography>
              <MDTypography variant="body2">
                De <strong>{dialogDiasRutura.valorAntigo}</strong> para{" "}
                <strong>{dialogDiasRutura.valorNovo}</strong> dias.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelarDiasRutura} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmarDiasRutura} color="info" variant="contained">
            Confirmar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDelEncOpen}
        onClose={() => setConfirmDelEncOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Apagar encomenda</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2">
            Pretende apagar esta encomenda? Esta ação não pode ser anulada.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setConfirmDelEncOpen(false)} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmDeleteEncomenda} color="error" variant="contained">
            Apagar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDelPreOpen}
        onClose={() => setConfirmDelPreOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Apagar pré-venda</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2">
            Pretende apagar esta pré-venda? Esta ação não pode ser anulada.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setConfirmDelPreOpen(false)} color="secondary" variant="text">
            Cancelar
          </MDButton>
          <MDButton onClick={handleConfirmDeletePreVenda} color="error" variant="contained">
            Apagar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog — Stocks/Vendas/Compras (MenuSearchMateriais) */}
      <MenuSearchMateriais
        open={detalhesOpen}
        onClose={handleCloseDetalhes}
        material={materialDetalhe}
        centrosSelecionados={centrosSel}
      />
    </DashboardLayout>
  );
}

export default AnaliseStocks;
